import * as THREE from "three";
import { useRef, useState, useEffect, Suspense } from "react";
import { useGLTF } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";

export default function ModelBackground({ onSelect, modelRefs, selectedModelName, controlsRef }) {
  // ✅ Card data with positions, rotations, and unique links
  const cardDataByName = {
    Elbphilharmonie: {
      src: "/images/cards/newimages/Mediengruppe Hamburg.png",
      from: [0, 1.3, 0],
      offset: [1.8, 1.3, 0.2],
      rotation: [0, 5, 0],
      size: [2.04, 1.32],
      link: "https://mediagroup-germany.com/floracard/", 
      // https://mediagroup-germany.com/mediengruppe-hamburg/  
    },
    Uploads2: {
      src: "/images/cards/newimages/Meta Global Capital USA.png",
      from: [1, 1.8, 0],
      offset: [0.5, 1.8, 1.5],
      rotation: [0, 5, 0],
      size: [2.88, 1.54],
      link: "https://mediagroup-germany.com/meta-global-capital/",
    },
    Uploads1: {
      src: "/images/cards/newimages/Godmode Media.png",
      from: [0, 0.6, 0],
      offset: [0.8, 0.6, -0.3],
      rotation: [0, 0, 0],
      size: [1.44, 0.74],
      link: "https://mediagroup-germany.com/godmode-media/",
    },
    Church: {
      src: "/images/cards/newimages/Mediengruppe München.png",
      from: [0, 0.6, -0.2],
      offset: [2.1, 0.6, -0.2],
      rotation: [0, 0, 0],
      size: [2.04, 1.14],
      link: "https://mediagroup-germany.com/mediengruppe-muenchen/",
    },
    BrandenburgGate: {
      src: "/images/cards/newimages/Mediengruppe Berlin.png",
      from: [0, 1, -0.8],
      offset: [1.7, 1.1, -0.8],
      rotation: [0, 0, 0],
      size: [2.52, 1.42],
      link: "https://mediagroup-germany.com/mediengruppe-berlin/",
    },
    Mnogohome: {
      src: "/images/cards/newimages/Erlangennew_card.png",
      from: [0, 0.8, 0],
      offset: [1.2, 0.9, 0],
      rotation: [0, 0, 0],
      size: [2.04, 1.14],
      link: "https://mediagroup-germany.com/partner/",
    },
    Para: {
      src: "/images/cards/newimages/Mediengruppe Zypern.png",
      from: [0, 0.9, 0],
      offset: [1.5, 0.9, -0.9],
      rotation: [0, 0, 0],
      size: [2.04, 1.14],
      link: "https://mediagroup-germany.com/mediengruppe-zypern/",
    },
    Building: {
      src: "/images/cards/newimages/Meta-Welt der Mediengruppe.png",
      from: [0, 1.1, 0],
      offset: [1, 1.1, -0.2],
      rotation: [0, 0, 0],
      size: [2.04, 1.14],
      link: "https://mediagroup-germany.com/meta-welt-der-mediengruppe/",
    },
    Sinwellturm: {
      src: "/images/cards/newimages/Mediengruppe Nürnberg.png",
      from: [0, 0.85, 0],
      offset: [0.1, 0.85, -1.8],
      rotation: [0, 1, 0],
      size: [3.06, 1.71],
      link: "https://mediengruppenuernberg.de/",
    },
  };

  // ✅ Texture loader with error handling
  function useRobustTexture(src) {
    const [texture, setTexture] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
      if (!src) return;
      const loader = new THREE.TextureLoader();
      loader.load(src, (loadedTex) => {
        loadedTex.flipY = true;
        setTexture(loadedTex);
        setIsLoaded(true);
      });
    }, [src]);

    return { texture, isLoaded };
  }

  // ✅ Billboard card with animation and clickable link
function BillboardCard({ name, controlsRef }) {
  const config = cardDataByName[name];
  const meshRef = useRef();
  const { texture, isLoaded } = useRobustTexture(config?.src || "");
  const [currentPos, setCurrentPos] = useState(config?.from || [0, 3, 0]);
  const [hasAnimated, setHasAnimated] = useState(false);

  useFrame(() => {
    if (!meshRef.current || !config) return;

    // If animation already completed, skip further animation
    if (hasAnimated) return;

    const target = new THREE.Vector3(...config.offset);
    const current = new THREE.Vector3(...currentPos);

    // Animate movement
    current.lerp(target, 0.1);
    meshRef.current.position.copy(current);
    setCurrentPos([current.x, current.y, current.z]);

    // Check if close enough to target to stop animating
    if (current.distanceTo(target) < 0.01) {
      setHasAnimated(true);
    }
  });

  const handleCardClick = (e) => {
    e.stopPropagation();
    if (config?.link) {
      console.log('Card clicked, opening:', config.link);
      window.open(config.link, "_blank", "noopener,noreferrer");
    }
  };

  const handlePointerOver = (e) => {
    e.stopPropagation();
    document.body.style.cursor = 'pointer';
    if (controlsRef?.current) {
      controlsRef.current.enabled = false;
    }
  };

  const handlePointerOut = (e) => {
    e.stopPropagation();
    document.body.style.cursor = 'default';
    if (controlsRef?.current) {
      controlsRef.current.enabled = true;
    }
  };

  const handlePointerMove = (e) => {
    e.stopPropagation();
  };

  useEffect(() => {
    // Reset animation if a *different* card name is passed
    setHasAnimated(false);
    setCurrentPos(config?.from || [0, 3, 0]);
  }, [name]);

  if (!isLoaded || !config) return null;

  return (
    <mesh
      ref={meshRef}
      rotation={config.rotation}
      onClick={handleCardClick}
      onPointerDown={(e) => {
        e.stopPropagation();
        handleCardClick(e);
      }}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
      onPointerMove={handlePointerMove}
      onPointerUp={(e) => e.stopPropagation()}
      position={currentPos}
    >
      <planeGeometry args={config.size} />
      <meshBasicMaterial
        map={texture}
        transparent
        opacity={0.9}
        depthWrite={false}
        depthTest
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}


  // ✅ Scene model data
  const modelsData = [
    { name: "Elbphilharmonie", path: "/models/modelcompressed/Elbphilharmonie.glb", scale: [2.5, 2.5, 2.5], position: [-18, -1.8, 3], rotation: [0, 1, 0] },
    { name: "Uploads2", path: "/models/modelcompressed/uploads_files_6102055_building_5.glb", scale: [0.6, 0.6, 0.6], position: [-2.8, -0.8, -1], rotation: [0, 0.8, 0] },
    { name: "Uploads1", path: "/models/modelcompressed/uploads_files_2437298_001.glb", scale: [1.44, 1.44, 1.44], position: [-6, -1.2, 0.3], rotation: [0, 0.5, 0] },
    { name: "Church", path: "/models/modelcompressed/Churche_Frauenkirche_Munich.glb", scale: [0.72, 0.72, 0.72], position: [-10, -0.9, 0.8], rotation: [0, -0.1, 0] },
    { name: "BrandenburgGate", path: "/models/modelcompressed/BrandenburgGate2.glb", scale: [0.84, 0.84, 0.84], position: [0, -0.74, 0], rotation: [0, 0, 0] },
    { name: "Mnogohome", path: "/models/modelcompressed/Erlangennew.glb", scale: [0.96, 0.96, 0.96], position: [2.9, -0.9, -1.3], rotation: [0, -0.3, 0] },
    { name: "Para", path: "/models/modelcompressed/para.glb", scale: [0.96, 0.96, 0.96], position: [7, -1.3, 0.3], rotation: [0, -0.5, 0] },
    { name: "Building", path: "/models/modelcompressed/sothebys_111w57_04.glb", scale: [1.3, 1.3, 1.3], position: [12, -1.3, 0.8], rotation: [0, 0, 0] },
    { name: "Sinwellturm", path: "/models/modelcompressed/Sinwellturm.glb", scale: [0.8, 0.8, 0.8], position: [17, -1, 2], rotation: [0, -1, 0] },
  ];

  // ✅ Render models and billboard cards
  return (
    <>
      {modelsData.map((modelProps, index) => {
        const model = useGLTF(modelProps.path);
        const groupRef = useRef();
        if (modelRefs) modelRefs.current[index] = groupRef;

        const handleClick = (event) => {
          event.stopPropagation();
          if (!groupRef.current) return;
          const box = new THREE.Box3().setFromObject(groupRef.current);
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());
          const radius = size.length() / 2;
          onSelect && onSelect(center, radius, modelProps.name);
        };

        return (
          <group
            key={index}
            ref={groupRef}
            scale={modelProps.scale}
            position={modelProps.position}
            rotation={modelProps.rotation}
            onClick={handleClick}
            onPointerMove={(e) => {
              if (selectedModelName === modelProps.name) {
                e.stopPropagation();
              }
            }}
          >
            <primitive object={model.scene.clone()} />
            {selectedModelName === modelProps.name && (
              <Suspense fallback={null}>
                <BillboardCard name={modelProps.name} controlsRef={controlsRef} />
              </Suspense>
            )}
          </group>
        );
      })}
    </>
  );
}
